import React, { useState, useEffect } from 'react'
import {
  User,
  Building2,
  Phone,
  Mail,
  MapPin,
  Calendar,
  FileText,
  Edit,
  Smartphone,
  Laptop,
  Wrench,
  ShoppingCart,
  TrendingUp,
  Clock,
  Euro
} from 'lucide-react'
import { Cliente, OrdemServico, Equipamento, Venda } from '../../types'
import { useToast } from '../Toast'
import clientesService from '../../services/clientes'
import Button from '../ui/Button'
import Table from '../ui/Table'
import ClienteModal from './ClienteModal'

interface ClienteDetailsProps {
  clienteId: number
  onEdit?: () => void
}

const ClienteDetails: React.FC<ClienteDetailsProps> = ({
  clienteId,
  onEdit
}) => {
  const { error: showError } = useToast()
  
  const [cliente, setCliente] = useState<Cliente | null>(null)
  const [stats, setStats] = useState<any>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [activeTab, setActiveTab] = useState<'equipamentos' | 'ordens' | 'vendas'>('equipamentos')
  const [showEditModal, setShowEditModal] = useState(false)

  useEffect(() => {
    loadClienteDetails()
  }, [clienteId])

  const loadClienteDetails = async () => {
    setIsLoading(true)
    try {
      const [clienteData, statsData] = await Promise.all([
        clientesService.getClienteById(clienteId),
        clientesService.getClienteStats(clienteId)
      ])
      
      setCliente(clienteData)
      setStats(statsData.stats)
    } catch (err: any) {
      showError('Erro ao carregar dados do cliente', err.message)
    } finally {
      setIsLoading(false)
    }
  }

  const handleClienteUpdate = (updatedCliente: Cliente) => {
    setCliente(updatedCliente)
    loadClienteDetails() // Recarregar stats
  }

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-PT', {
      style: 'currency',
      currency: 'EUR'
    }).format(value)
  }

  const formatDate = (date: string) => {
    return new Intl.DateTimeFormat('pt-PT').format(new Date(date))
  }

  if (isLoading) {
    return (
      <div className="space-y-6 animate-pulse">
        {/* Header skeleton */}
        <div className="bg-white p-6 rounded-lg border border-gray-200">
          <div className="flex items-start justify-between">
            <div className="flex space-x-4">
              <div className="w-16 h-16 bg-gray-200 rounded-full"></div>
              <div className="space-y-2">
                <div className="h-6 bg-gray-200 rounded w-48"></div>
                <div className="h-4 bg-gray-200 rounded w-32"></div>
              </div>
            </div>
            <div className="h-9 bg-gray-200 rounded w-24"></div>
          </div>
        </div>

        {/* Stats skeleton */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="bg-white p-4 rounded-lg border border-gray-200">
              <div className="h-4 bg-gray-200 rounded w-16 mb-2"></div>
              <div className="h-6 bg-gray-200 rounded w-20"></div>
            </div>
          ))}
        </div>
      </div>
    )
  }

  if (!cliente) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Cliente não encontrado</p>
      </div>
    )
  }

  // Colunas da tabela de equipamentos
  const equipamentosColumns = [
    {
      key: 'tipo',
      title: 'Tipo',
      render: (value: any, equipamento: Equipamento) => (
        <div className="flex items-center space-x-2">
          <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center">
            {equipamento.modelo.tipoEquipamento.nome === 'Smartphone' && <Smartphone className="h-4 w-4" />}
            {equipamento.modelo.tipoEquipamento.nome === 'Portátil' && <Laptop className="h-4 w-4" />}
          </div>
          <span className="font-medium">{equipamento.modelo.tipoEquipamento.nome}</span>
        </div>
      )
    },
    {
      key: 'marca',
      title: 'Marca/Modelo',
      render: (value: any, equipamento: Equipamento) => (
        <div>
          <div className="font-medium">{equipamento.modelo.marca.nome}</div>
          <div className="text-sm text-gray-500">{equipamento.modelo.nome}</div>
        </div>
      )
    },
    {
      key: 'numeroSerie',
      title: 'Número de Série',
      render: (value: string) => value || '—'
    },
    {
      key: 'estadoFisico',
      title: 'Estado',
      render: (value: string) => (
        <span className={`
          inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium
          ${value === 'EXCELENTE' ? 'bg-green-100 text-green-800' : ''}
          ${value === 'BOM' ? 'bg-blue-100 text-blue-800' : ''}
          ${value === 'USADO' ? 'bg-yellow-100 text-yellow-800' : ''}
          ${value === 'DANIFICADO' ? 'bg-red-100 text-red-800' : ''}
        `}>
          {value.charAt(0) + value.slice(1).toLowerCase()}
        </span>
      )
    }
  ]

  // Colunas da tabela de ordens de serviço
  const ordensColumns = [
    {
      key: 'numero',
      title: 'Número',
      render: (value: string) => (
        <span className="font-mono font-medium">{value}</span>
      )
    },
    {
      key: 'equipamento',
      title: 'Equipamento',
      render: (value: any, ordem: OrdemServico) => (
        <div>
          <div className="font-medium">{ordem.equipamento.modelo.marca.nome}</div>
          <div className="text-sm text-gray-500">{ordem.equipamento.modelo.nome}</div>
        </div>
      )
    },
    {
      key: 'estado',
      title: 'Estado',
      render: (value: any, ordem: OrdemServico) => (
        <span 
          className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
          style={{ 
            backgroundColor: `${ordem.estado.cor}20`,
            color: ordem.estado.cor 
          }}
        >
          {ordem.estado.nome}
        </span>
      )
    },
    {
      key: 'valorFinal',
      title: 'Valor',
      align: 'right' as const,
      render: (value: number) => formatCurrency(value)
    },
    {
      key: 'dataEntrada',
      title: 'Data',
      render: (value: string) => formatDate(value)
    }
  ]

  // Colunas da tabela de vendas
  const vendasColumns = [
    {
      key: 'numero',
      title: 'Número',
      render: (value: string) => (
        <span className="font-mono font-medium">{value}</span>
      )
    },
    {
      key: 'total',
      title: 'Total',
      align: 'right' as const,
      render: (value: number) => formatCurrency(value)
    },
    {
      key: 'createdAt',
      title: 'Data',
      render: (value: string) => formatDate(value)
    }
  ]

  return (
    <div className="space-y-6">
      {/* Header do Cliente */}
      <div className="bg-white p-6 rounded-lg border border-gray-200">
        <div className="flex items-start justify-between">
          <div className="flex space-x-4">
            <div className={`
              w-16 h-16 rounded-full flex items-center justify-center text-white text-2xl font-bold
              ${cliente.tipoCliente === 'EMPRESA' ? 'bg-blue-500' : 'bg-green-500'}
            `}>
              {cliente.tipoCliente === 'EMPRESA' ? (
                <Building2 className="h-8 w-8" />
              ) : (
                <User className="h-8 w-8" />
              )}
            </div>
            
            <div>
              <h1 className="text-2xl font-bold text-gray-900">{cliente.nome}</h1>
              {cliente.nomeComercial && (
                <p className="text-lg text-gray-600">{cliente.nomeComercial}</p>
              )}
              <div className="flex items-center space-x-4 mt-2">
                <span className={`
                  inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium
                  ${cliente.tipoCliente === 'EMPRESA' 
                    ? 'bg-blue-100 text-blue-800' 
                    : 'bg-green-100 text-green-800'
                  }
                `}>
                  {cliente.tipoCliente === 'EMPRESA' ? 'Empresa' : 'Pessoa Individual'}
                </span>
                
                {!cliente.ativo && (
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                    Inativo
                  </span>
                )}
              </div>
            </div>
          </div>
          
          <Button
            variant="secondary"
            leftIcon={<Edit className="h-4 w-4" />}
            onClick={() => setShowEditModal(true)}
          >
            Editar
          </Button>
        </div>

        {/* Informações de Contacto */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mt-6 pt-6 border-t border-gray-200">
          {cliente.telefonePrincipal && (
            <div className="flex items-center space-x-2 text-gray-600">
              <Phone className="h-4 w-4" />
              <span>{cliente.telefonePrincipal}</span>
            </div>
          )}
          
          {cliente.email && (
            <div className="flex items-center space-x-2 text-gray-600">
              <Mail className="h-4 w-4" />
              <span>{cliente.email}</span>
            </div>
          )}
          
          {cliente.cidade && cliente.distrito && (
            <div className="flex items-center space-x-2 text-gray-600">
              <MapPin className="h-4 w-4" />
              <span>{cliente.cidade}, {cliente.distrito}</span>
            </div>
          )}
          
          <div className="flex items-center space-x-2 text-gray-600">
            <Calendar className="h-4 w-4" />
            <span>Cliente desde {formatDate(cliente.createdAt)}</span>
          </div>
        </div>

        {/* Documentação */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
          {cliente.nif && (
            <div>
              <span className="text-sm font-medium text-gray-500">NIF:</span>
              <span className="ml-2 font-mono">{cliente.nif}</span>
            </div>
          )}
          
          
          {cliente.ccNumero && (
            <div>
              <span className="text-sm font-medium text-gray-500">CC:</span>
              <span className="ml-2 font-mono">{cliente.ccNumero}</span>
            </div>
          )}
        </div>

        {/* Notas */}
        {cliente.notas && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <div className="flex items-start space-x-2">
              <FileText className="h-4 w-4 text-gray-400 mt-0.5" />
              <div>
                <h4 className="text-sm font-medium text-gray-900">Notas</h4>
                <p className="text-sm text-gray-600 mt-1">{cliente.notas}</p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Estatísticas */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white p-4 rounded-lg border border-gray-200">
            <div className="flex items-center space-x-2">
              <Smartphone className="h-5 w-5 text-blue-500" />
              <span className="text-sm font-medium text-gray-600">Equipamentos</span>
            </div>
            <p className="text-2xl font-bold text-gray-900 mt-1">{stats.totalEquipamentos}</p>
          </div>

          <div className="bg-white p-4 rounded-lg border border-gray-200">
            <div className="flex items-center space-x-2">
              <Wrench className="h-5 w-5 text-green-500" />
              <span className="text-sm font-medium text-gray-600">Ordens Serviço</span>
            </div>
            <p className="text-2xl font-bold text-gray-900 mt-1">{stats.totalOrdens}</p>
          </div>

          <div className="bg-white p-4 rounded-lg border border-gray-200">
            <div className="flex items-center space-x-2">
              <ShoppingCart className="h-5 w-5 text-purple-500" />
              <span className="text-sm font-medium text-gray-600">Vendas</span>
            </div>
            <p className="text-2xl font-bold text-gray-900 mt-1">{stats.totalVendas}</p>
          </div>

          <div className="bg-white p-4 rounded-lg border border-gray-200">
            <div className="flex items-center space-x-2">
              <Euro className="h-5 w-5 text-yellow-500" />
              <span className="text-sm font-medium text-gray-600">Total Faturado</span>
            </div>
            <p className="text-2xl font-bold text-gray-900 mt-1">
              {formatCurrency(stats.valorTotal)}
            </p>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="bg-white rounded-lg border border-gray-200">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8 px-6">
            {[
              { key: 'equipamentos', label: 'Equipamentos', icon: Smartphone },
              { key: 'ordens', label: 'Ordens de Serviço', icon: Wrench },
              { key: 'vendas', label: 'Vendas', icon: ShoppingCart }
            ].map(({ key, label, icon: Icon }) => (
              <button
                key={key}
                onClick={() => setActiveTab(key as any)}
                className={`
                  group inline-flex items-center py-4 px-1 border-b-2 font-medium text-sm
                  ${activeTab === key
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }
                `}
              >
                <Icon className="mr-2 h-4 w-4" />
                {label}
              </button>
            ))}
          </nav>
        </div>

        <div className="p-6">
          {activeTab === 'equipamentos' && (
            <Table
              data={cliente.equipamentos || []}
              columns={equipamentosColumns}
              keyField="id"
              emptyMessage="Nenhum equipamento cadastrado"
            />
          )}

          {activeTab === 'ordens' && (
            <Table
              data={cliente.ordensServico || []}
              columns={ordensColumns}
              keyField="id"
              emptyMessage="Nenhuma ordem de serviço encontrada"
            />
          )}

          {activeTab === 'vendas' && (
            <Table
              data={cliente.vendas || []}
              columns={vendasColumns}
              keyField="id"
              emptyMessage="Nenhuma venda encontrada"
            />
          )}
        </div>
      </div>

      {/* Modal de Edição */}
      <ClienteModal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        cliente={cliente}
        onSave={handleClienteUpdate}
      />
    </div>
  )
}

export default ClienteDetails